import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Stat, Card, CardBody, Badge } from "../../components/admin/UI";
import { Users, Trophy, ClipboardCheck, BarChart3, Megaphone } from "lucide-react";
import { toast } from "sonner";
import PrivatePlayerPhoto from "../../components/PrivatePlayerPhoto";
import PrivateTeamPhoto from "../../components/PrivateTeamPhoto";

export default function CoachDashboard() {
    const { user, activeClubId, activeSeasonId } = useAuth();
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [matches, setMatches] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [attendanceRate, setAttendanceRate] = useState(null);
    const [photoState, setPhotoState] = useState({});
    const fileInputs = useRef({});

    const photoKey = (type, id) => `${type}:${id}`;
    const photoAvailable = useCallback((type, id, available) => {
        const key = photoKey(type, id);
        setPhotoState((current) => current[key]?.available === available
            ? current
            : { ...current, [key]: { ...current[key], available } });
    }, []);
    const teamPhotoAvailable = useCallback(
        (id, available) => photoAvailable("teams", id, available), [photoAvailable]
    );
    const playerPhotoAvailable = useCallback(
        (id, available) => photoAvailable("players", id, available), [photoAvailable]
    );

    const uploadPhoto = async (type, id, file) => {
        const key = photoKey(type, id);
        if (!file || photoState[key]?.busy) return;
        setPhotoState((current) => ({ ...current, [key]: { ...current[key], busy: true } }));
        const data = new FormData();
        data.append("file", file);
        try {
            await api.put(`/${type}/${id}/photo`, data, { params: { club_id: activeClubId } });
            setPhotoState((current) => ({ ...current, [key]: {
                available: true, busy: false, version: (current[key]?.version || 0) + 1,
            } }));
            toast.success("Photo enregistrée");
        } catch (error) {
            setPhotoState((current) => ({ ...current, [key]: { ...current[key], busy: false } }));
            toast.error(formatApiError(error));
        } finally {
            if (fileInputs.current[key]) fileInputs.current[key].value = "";
        }
    };

    const removePhoto = async (type, id) => {
        const key = photoKey(type, id);
        if (photoState[key]?.busy || !window.confirm("Supprimer cette photo ?")) return;
        setPhotoState((current) => ({ ...current, [key]: { ...current[key], busy: true } }));
        try {
            await api.delete(`/${type}/${id}/photo`, { params: { club_id: activeClubId } });
            setPhotoState((current) => ({ ...current, [key]: {
                available: false, busy: false, version: (current[key]?.version || 0) + 1,
            } }));
            toast.success("Photo supprimée");
        } catch (error) {
            setPhotoState((current) => ({ ...current, [key]: { ...current[key], busy: false } }));
            toast.error(formatApiError(error));
        }
    };

    const photoControls = (type, id) => {
        const key = photoKey(type, id);
        const state = photoState[key] || {};
        return <div className="flex flex-wrap items-center gap-3 mt-2">
            <input ref={(node) => { fileInputs.current[key] = node; }} type="file"
                accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={(event) => uploadPhoto(type, id, event.target.files?.[0])} />
            <button type="button" disabled={state.busy} onClick={() => fileInputs.current[key]?.click()}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50">
                {state.busy ? "Chargement…" : state.available ? "Changer la photo" : "Ajouter une photo"}
            </button>
            {state.available && <button type="button" disabled={state.busy}
                onClick={() => removePhoto(type, id)} className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50">
                Supprimer la photo
            </button>}
        </div>;
    };

    useEffect(() => {
        if (!activeClubId || !activeSeasonId || !user?.id) return;

        const params = { club_id: activeClubId, season_id: activeSeasonId };

        const load = async () => {
            const teamsRes = await api.get("/teams", { params });
            const myTeams = teamsRes.data.filter((t) => t.coach_user_id === user.id);
            setTeams(myTeams);

            const [playersRes, matchesRes, annRes] = await Promise.all([
                api.get("/players", { params }).catch(() => ({ data: [] })),
                api.get("/matches", { params }).catch(() => ({ data: [] })),
                api.get("/comms/announcements", { params: { club_id: activeClubId } }).catch(() => ({ data: [] })),
            ]);

            setPlayers(playersRes.data || []);
            setMatches(matchesRes.data || []);
            setAnnouncements((annRes.data || []).slice(0, 5));

            const rates = [];
            for (const team of myTeams) {
                const res = await api.get(`/attendance/stats/team/${team.id}`, { params });
                const teamStats = res.data || [];
                teamStats.forEach((s) => {
                    if (typeof s.attendance_rate === "number") rates.push(s.attendance_rate);
                });
            }

            if (rates.length) {
                const avg = rates.reduce((a, b) => a + b, 0) / rates.length;
                setAttendanceRate(Math.round(avg * 10) / 10);
            } else {
                setAttendanceRate(null);
            }
        };

        load();
    }, [activeClubId, activeSeasonId, user?.id]);

    const myTeamIds = teams.map((t) => t.id);
    const myPlayers = players.filter((p) => myTeamIds.includes(p.team_id));
    const myMatches = matches.filter((m) => myTeamIds.includes(m.team_id));
    const upcomingMatches = myMatches.filter((m) => m.status !== "played").slice(0, 5);

    return (
        <div data-testid="coach-dashboard-page">
            <PageHeader
                title={`Bonjour ${user?.full_name || "Coach"}`}
                subtitle="Votre espace coach : équipes, joueurs, présences, matchs et statistiques."
            />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat label="Mes équipes" value={teams.length} icon={Trophy} accent="orange" />
                <Stat label="Mes joueurs" value={myPlayers.length} icon={Users} accent="slate" />
                <Stat label="Matchs" value={myMatches.length} icon={BarChart3} accent="slate" />
                <Stat
                    label="Présence moyenne"
                    value={attendanceRate === null ? "—" : `${attendanceRate}%`}
                    icon={ClipboardCheck}
                    accent="emerald"
                />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Card>
                    <CardBody>
                        <h3 className="font-display text-xl font-bold mb-4">Mes équipes</h3>
                        {teams.length === 0 ? (
                            <p className="text-sm text-slate-500">Aucune équipe assignée pour le moment.</p>
                        ) : (
                            <div className="space-y-3">
                                {teams.map((t) => (
                                    <div key={t.id} className="bg-slate-50 rounded-lg p-3">
                                        <PrivateTeamPhoto team={t} clubId={activeClubId}
                                            refreshVersion={photoState[photoKey("teams", t.id)]?.version || 0}
                                            knownAbsent={photoState[photoKey("teams", t.id)]?.available === false}
                                            onAvailabilityChange={teamPhotoAvailable} />
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <div className="font-semibold text-slate-900">{t.name}</div>
                                            <div className="text-xs text-slate-500">{t.category}</div>
                                          </div>
                                          <Badge color="orange">{t.gender}</Badge>
                                        </div>
                                        {photoControls("teams", t.id)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>

                <Card className="lg:col-span-2">
                    <CardBody>
                        <h3 className="font-display text-xl font-bold mb-4">Mes joueurs</h3>
                        {myPlayers.length === 0 ? <p className="text-sm text-slate-500">Aucun joueur assigné.</p> : (
                            <div className="grid sm:grid-cols-2 gap-3">
                                {myPlayers.map((player) => <div key={player.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                                    <PrivatePlayerPhoto player={player} clubId={activeClubId}
                                        refreshVersion={photoState[photoKey("players", player.id)]?.version || 0}
                                        knownAbsent={photoState[photoKey("players", player.id)]?.available === false}
                                        onAvailabilityChange={playerPhotoAvailable} />
                                    <div className="min-w-0">
                                        <div className="font-semibold text-slate-900">{player.first_name} {player.last_name}</div>
                                        {photoControls("players", player.id)}
                                    </div>
                                </div>)}
                            </div>
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h3 className="font-display text-xl font-bold mb-4">Prochains matchs</h3>
                        {upcomingMatches.length === 0 ? (
                            <p className="text-sm text-slate-500">Aucun match à venir.</p>
                        ) : (
                            <div className="space-y-3">
                                {upcomingMatches.map((m) => (
                                    <div key={m.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                                        <div>
                                            <div className="font-semibold text-slate-900">ASSMBB vs {m.opponent}</div>
                                            <div className="text-xs text-slate-500">{m.date} {m.time ? `· ${m.time}` : ""}</div>
                                        </div>
                                        <Badge color="blue">À venir</Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>

                <Card className="lg:col-span-2">
                    <CardBody>
                        <div className="flex items-center gap-2 mb-4">
                            <Megaphone className="w-4 h-4 text-orange-500" />
                            <h3 className="font-display text-xl font-bold">Dernières annonces</h3>
                        </div>

                        {announcements.length === 0 ? (
                            <p className="text-sm text-slate-500">Aucune annonce récente.</p>
                        ) : (
                            <div className="space-y-3">
                                {announcements.map((a) => (
                                    <div key={a.id} className="border-b border-slate-100 pb-3 last:border-0">
                                        <div className="font-semibold text-slate-900">{a.title}</div>
                                        <p className="text-sm text-slate-500 line-clamp-2">{a.content}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
}
